import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Hash, Mail, MessageCircle, Phone, Sparkles, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import DashboardPage from './DashboardPage'
import api from '../../services/api'

interface Participant { personRef: string; displayName: string | null; party: string; role: string; tone: number; reaction?: string | null; toneLabel?: string | null; toneSentiment?: number | null }
interface TimelineEvent { id: string; source: string; speaker: string; sentiment: number; snippet: string; atIso: string; sourceRef: unknown; deepLink?: string | null }
interface IncidentSource { type: string; occurredAtIso: string; deepLink: string | null }
interface IncidentData {
  incident: { id: string; kind: string; party: string; polarity: string; severity: string; title: string; hook: string; narrative: string; suggestedStep: string | null; windowStart: string; windowEnd: string }
  source?: IncidentSource | null
  participants: Participant[]
  timeline: TimelineEvent[]
}

function sourceIcon(type: string): LucideIcon {
  switch (type) {
    case 'call':
    case 'phone':
      return Phone
    case 'dm':
      return MessageCircle
    case 'channel':
      return Hash
    case 'mail':
      return Mail
    default:
      return Sparkles
  }
}

export default function EmotionIncidentView() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<IncidentData | null>(null)

  useEffect(() => {
    if (!id) return
    api.get(`/workspace/service/incident/${id}`).then((r) => setData(r.data)).catch(() => setData(null))
  }, [id])

  if (!data) return <DashboardPage title="Investigation"><p className="text-sm text-muted-foreground">loading…</p></DashboardPage>
  const { incident, source, participants, timeline } = data
  const SourceIcon = source ? sourceIcon(source.type) : Sparkles

  return (
    <DashboardPage title={incident.title} subtitle={`${incident.kind === 'trend' ? 'trend' : 'incident'} · ${incident.severity}`}>
      <button onClick={() => navigate('/workspace/service')} className="inline-flex items-center gap-1.5 mb-4 text-sm text-muted-foreground hover:text-foreground transition duration-150">
        <ArrowLeft size={16} />back
      </button>

      {source && (
        <div className="flex items-center justify-between gap-3 rounded-lg ring-1 ring-border bg-card p-3 mb-4">
          <span className="flex items-center gap-2.5">
            <SourceIcon size={18} className="text-muted-foreground" />
            <span>
              <span className="block text-sm font-medium text-foreground">{source.type}</span>
              <span className="block text-xs text-muted-foreground">{new Date(source.occurredAtIso).toLocaleString()}</span>
            </span>
          </span>
          {source.deepLink ? (
            <button onClick={() => navigate(source.deepLink!)} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
              open source <ExternalLink size={14} />
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">no link</span>
          )}
        </div>
      )}

      <div className="rounded-lg ring-1 ring-border bg-card p-4 mb-6">
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2"><Sparkles size={16} />what happened — AI</p>
        <p className="leading-relaxed text-foreground mb-3">{incident.narrative}</p>
        {incident.suggestedStep && (
          <p className="text-sm text-foreground"><span className="text-muted-foreground">suggested step: </span>{incident.suggestedStep}</p>
        )}
      </div>

      <h2 className="flex items-center gap-2 text-base font-medium text-foreground mb-3"><Users size={18} />Participants</h2>
      <div className="flex flex-wrap gap-2 mb-6">
        {participants.map((p) => (
          <div key={p.personRef} className="flex items-center gap-2 p-2.5 rounded-lg ring-1 ring-border bg-card">
            <span>
              <span className="block font-medium text-foreground">{p.displayName ?? p.personRef}</span>
              <span className="block text-xs text-muted-foreground">{p.role} · tone {p.tone.toFixed(1)}</span>
              {p.reaction && (
                <span className="block text-xs text-muted-foreground"><span className="text-muted-foreground">reaction: </span>{p.reaction}</span>
              )}
              {p.toneLabel && (
                <span className="block text-xs text-muted-foreground"><span className="text-muted-foreground">voice tone: </span>{p.toneLabel}</span>
              )}
            </span>
          </div>
        ))}
      </div>

      <h2 className="text-base font-medium text-foreground mb-1">Timeline · evidence</h2>
      <p className="text-sm text-muted-foreground mb-3">unified feed across all sources</p>
      <div className="flex flex-col gap-3">
        {timeline.map((e) => (
          <div key={e.id} className="flex gap-3">
            <span className="flex-none w-16 text-xs text-muted-foreground">{new Date(e.atIso).toLocaleDateString()}</span>
            <span className="flex-1">
              <span className="block text-xs text-muted-foreground">{e.source} · {e.speaker}</span>
              <span className="block text-foreground">{e.snippet}</span>
              {e.deepLink && (
                <button onClick={() => navigate(e.deepLink!)} className="inline-flex items-center gap-1 mt-0.5 text-xs text-primary hover:underline">
                  open <ExternalLink size={12} />
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    </DashboardPage>
  )
}
